import { useState, useEffect, useCallback } from "react";
import {
  collection, addDoc, deleteDoc, updateDoc,
  doc, query, orderBy, onSnapshot, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

// Hook de "Huchas": metas de ahorro nombradas, con saldo propio que se
// acumula con el tiempo (NUNCA se resetea solo, a diferencia de los
// presupuestos mensuales). Aportar/retirar crea también un movimiento real
// en el historial (entries), porque ese dinero de verdad sale o vuelve al
// balance general.
export function useHuchas() {
  const { user, profile } = useAuth();
  const [huchas, setHuchas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Igual que entries/pendientes: vive en el workspace activo, con
  // compatibilidad hacia atrás si por algún motivo aún no hay workspace.
  const workspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId || null;
  const huchasRef = useCallback(() => {
    if (workspaceId) return collection(db, "workspaces", workspaceId, "huchas");
    return collection(db, "users", user.uid, "huchas");
  }, [workspaceId, user]);
  const huchaDoc = useCallback((id) => {
    if (workspaceId) return doc(db, "workspaces", workspaceId, "huchas", id);
    return doc(db, "users", user.uid, "huchas", id);
  }, [workspaceId, user]);

  useEffect(() => {
    // IMPORTANTE: se espera a que workspaceId esté disponible (no solo
    // "user") antes de suscribirse. Si solo se comprobara "user", en la
    // breve ventana en que el perfil aún no ha terminado de cargar
    // (workspaceId todavía null) este efecto caería en la ruta de
    // compatibilidad users/{uid}/huchas, para la que nunca se dio permiso
    // en las reglas de Firestore (solo existe la regla para
    // workspaces/{id}/huchas) — eso producía un permission-denied
    // puntual pero repetido cada vez que se entraba en Panel/Añadir
    // entrada/Huchas antes de que el perfil completo estuviera listo.
    if (!user || !workspaceId) {
      setLoading(!!user); // si hay user pero aún no hay workspaceId, seguimos "cargando" en vez de dar error
      return;
    }
    const ref = huchasRef();
    const q = query(ref, orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      snap => {
        setHuchas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (error) => {
        console.error("Error cargando huchas:", error);
        setLoading(false);
      }
    );
    return unsub;
  }, [user, workspaceId]);

  // Crea una hucha nueva. goal es opcional (null = sin meta definida, solo
  // acumula libremente y se muestra sin barra de progreso con tope).
  async function addHucha({ name, goal, color, icon }) {
    if (!user) return;
    await addDoc(huchasRef(), {
      name,
      goal: goal ? parseFloat(goal) : null,
      saldo: 0,
      color: color || "#7c3aed",
      icon: icon || "🐷",
      movimientos: [], // historial ligero: { type:"aporte"|"retiro", amount, date }
      addedBy: user.uid,
      createdAt: serverTimestamp(),
    });
  }

  async function deleteHucha(id) {
    await deleteDoc(huchaDoc(id));
  }

  async function updateHucha(id, data) {
    await updateDoc(huchaDoc(id), data);
  }

  function fmtDatetime(d) {
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  }

  // Aporta dinero a una hucha: sube su saldo y crea un GASTO real en el
  // historial general (resta del balance), con categoría = nombre de la
  // hucha, para que quede identificado de cuál se trata en el historial.
  // Se guarda el "entryId" de esa entrada junto al movimiento — así, si esa
  // entrada se borra alguna vez desde el Historial, se puede localizar y
  // revertir este movimiento (ver deleteHuchaMovementByEntryId más abajo).
  async function aportar(hucha, monto, addEntry) {
    const cantidad = parseFloat(monto);
    if (!cantidad || cantidad <= 0) return;
    const now = new Date();
    const dt = fmtDatetime(now);

    const result = await addEntry({
      type: "expense",
      cat: `🐷 ${hucha.name}`,
      desc: `Aporte a hucha: ${hucha.name}`,
      amount: cantidad,
      month: MONTHS[now.getMonth()],
      year: now.getFullYear(),
      datetime: dt,
      isHuchaMovement: true,
      huchaId: hucha.id,
    });

    const nuevosMovimientos = [...(hucha.movimientos || []), { type: "aporte", amount: cantidad, date: dt, entryId: result?.id || null }];
    await updateDoc(huchaDoc(hucha.id), {
      saldo: (hucha.saldo || 0) + cantidad,
      movimientos: nuevosMovimientos,
    });
  }

  // Retira dinero de una hucha: baja su saldo y crea un INGRESO real en el
  // historial general (suma al balance). Se puede retirar en cualquier
  // momento, sin necesidad de haber alcanzado la meta. Igual que en
  // aportar(), se guarda el "entryId" para poder revertir si se borra.
  async function retirar(hucha, monto, addEntry) {
    const cantidad = parseFloat(monto);
    if (!cantidad || cantidad <= 0) return;
    const saldoActual = hucha.saldo || 0;
    const cantidadReal = Math.min(cantidad, saldoActual); // no se puede retirar más de lo que hay
    if (cantidadReal <= 0) return;

    const now = new Date();
    const dt = fmtDatetime(now);

    const result = await addEntry({
      type: "income",
      cat: `🐷 ${hucha.name}`,
      desc: `Retiro de hucha: ${hucha.name}`,
      amount: cantidadReal,
      month: MONTHS[now.getMonth()],
      year: now.getFullYear(),
      datetime: dt,
      isHuchaMovement: true,
      huchaId: hucha.id,
    });

    const nuevosMovimientos = [...(hucha.movimientos || []), { type: "retiro", amount: cantidadReal, date: dt, entryId: result?.id || null }];
    await updateDoc(huchaDoc(hucha.id), {
      saldo: saldoActual - cantidadReal,
      movimientos: nuevosMovimientos,
    });
  }

  // Camino inverso: cuando se borra desde el Historial una entrada que
  // resultó de un aporte/retiro de hucha, hay que revertir su efecto aquí
  // también — si no, el dinero se queda "fantasma" en la hucha aunque el
  // movimiento real que lo originó ya no exista. Como los movimientos viven
  // dentro de un array en el propio documento de la hucha (no son
  // documentos propios consultables por Firestore), se busca en memoria
  // sobre las huchas ya cargadas.
  async function deleteHuchaMovementByEntryId(entryId) {
    if (!entryId) return;
    const hucha = huchas.find(h => (h.movimientos || []).some(m => m.entryId === entryId));
    if (!hucha) return;

    const mov = hucha.movimientos.find(m => m.entryId === entryId);
    const nuevosMovimientos = hucha.movimientos.filter(m => m.entryId !== entryId);
    // Revertir el efecto: si era un aporte, se resta del saldo; si era un
    // retiro, se vuelve a sumar.
    const saldoActual = hucha.saldo || 0;
    const nuevoSaldo = mov.type === "aporte"
      ? saldoActual - mov.amount
      : saldoActual + mov.amount;

    await updateDoc(huchaDoc(hucha.id), {
      saldo: nuevoSaldo,
      movimientos: nuevosMovimientos,
    });
  }

  function getProgress(hucha) {
    if (!hucha.goal || hucha.goal <= 0) return null; // sin meta = sin porcentaje
    return Math.min(100, Math.round(((hucha.saldo || 0) / hucha.goal) * 100));
  }

  return {
    huchas, loading,
    addHucha, deleteHucha, updateHucha,
    aportar, retirar, getProgress,
    deleteHuchaMovementByEntryId,
  };
}